import { useState, useEffect, lazy, Suspense } from "react";
import { Link, NavLink, useNavigate, useLocation } from "@/lib/router-compat";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { List as Menu, Plus, Moon, Sun, Heart, ChatCircle as MessageCircle } from "@phosphor-icons/react";
import { useTheme } from "next-themes";
import { ChevronDown } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
// Social icons moved to footer — kept imports for mobile menu if needed later
// MegaMenu is desktop-only (rendered inside `hidden md:flex`) and pulls in framer-motion.
// Lazy-loading it keeps framer-motion out of the main bundle → big win for mobile INP/LCP.
const MegaMenu = lazy(() => import("@/components/layout/MegaMenu").then(m => ({ default: m.MegaMenu })));

import { useAdminCheck } from "@/hooks/useAdminCheck";
import { useUserAccess } from "@/hooks/useUserAccess";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { OptimizedImage } from "@/components/ui/optimized-image";
import { NotificationCenter } from "@/components/notifications/NotificationCenter";
import { CountrySelector } from "@/components/layout/CountrySelector";
import { useCountry } from "@/hooks/useCountry";
import { useTranslationV2 } from "@/hooks/useTranslationV2";
import { setCountryCookie } from "@/utils/countryCookie";
import { CountryCode } from "@/utils/countryConfig";
import { COUNTRY_ROUTES } from "@/utils/countryRoutes";
import logoUrl from "@/assets/ro-auto-market-logo.svg";
import { useGsapLogoReveal } from "@/hooks/useGsapLogoReveal";

const linkClass = "inline-flex items-center justify-center rounded-md px-4 py-3 min-h-12 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background hover:bg-accent";

export default function Header() {
  const logoRevealRef = useGsapLogoReveal<HTMLImageElement>();
  const [isOpen, setIsOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const { isAdmin } = useAdminCheck();
  const { user, accessLevel, canAccessPremium, canCreateListings, isLoading, refreshAccess } = useUserAccess();
  const { isDealer, isBuyer } = useUserRole();
  const { toast } = useToast();
  const { theme, setTheme } = useTheme();
  const { country, config } = useCountry();
  const { t } = useTranslationV2('navigation');
  const basePath = config.path === '/' ? '' : config.path;
  const navigate = useNavigate();
  const { pathname: currentPathname } = useLocation();
  const isActivePath = (path: string) => currentPathname === path;

  useEffect(() => {
    if (user) {
      loadFavoritesCount(user.id);
      loadUnreadMessagesCount();
      
      // Listen for favorite changes
      const handleStorageChange = () => {
        if (user) loadFavoritesCount(user.id);
      };
      window.addEventListener('storage', handleStorageChange);
      
      // Set up realtime subscription for message updates
      const channel = supabase
        .channel(`header-messages-${Math.random().toString(36).slice(2)}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'messages'
          },
          () => {
            loadUnreadMessagesCount();
          }
        )
        .subscribe();
      
      return () => {
        window.removeEventListener('storage', handleStorageChange);
        supabase.removeChannel(channel);
      };
    }
    return undefined;
  }, [user]);

  const loadFavoritesCount = (userId: string) => {
    try {
      const favorites = localStorage.getItem(`favorites_${userId}`);
      if (favorites) {
        const count = JSON.parse(favorites).length;
        setFavoritesCount(count);
      } else {
        setFavoritesCount(0);
      }
    } catch (error) {
      console.error('Error loading favorites count:', error);
    }
  };

  const loadUnreadMessagesCount = async () => {
    try {
      if (!user) return;
      
      const { data, error } = await supabase
        .from('conversations')
        .select('unread_count_buyer, unread_count_seller, buyer_id, seller_id')
        .or(`buyer_id.eq.${user.id},seller_id.eq.${user.id}`);

      if (error) throw error;

      const totalUnread = (data || []).reduce((sum, conv) => {
        const unread = conv.buyer_id === user.id 
          ? conv.unread_count_buyer 
          : conv.unread_count_seller;
        return sum + (unread || 0);
      }, 0);

      setUnreadMessagesCount(totalUnread);
    } catch (error) {
      console.error('Error loading unread messages count:', error);
    }
  };


  const cleanupAuthState = () => {
    try {
      // Remove standard auth tokens
      localStorage.removeItem('supabase.auth.token');
      // Remove all Supabase auth keys from localStorage
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
          localStorage.removeItem(key);
        }
      });
      // Remove from sessionStorage if in use
      Object.keys(sessionStorage || {}).forEach((key) => {
        if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
          sessionStorage.removeItem(key);
        }
      });
    } catch (error) {
      // Ignore cleanup errors
    }
  };

  const handleSyncSubscription = async () => {
    try {
      setIsSyncing(true);
      const { data, error } = await supabase.functions.invoke('check-subscription');
      if ((error as any)) throw error;
      await refreshAccess();
      const subscribed = (data as any)?.subscribed;
      const tier = (data as any)?.subscription_tier;
      const end = (data as any)?.subscription_end;
      toast({
        title: subscribed ? "Abonament sincronizat: activ" : "Abonament sincronizat: inactiv",
        description: subscribed ? `Plan: ${tier} • Expiră: ${end ? new Date(end).toLocaleString() : '-'}` : "Nu există abonament activ.",
      });
    } catch (e: any) {
      toast({ title: "Eroare la sincronizare", description: e?.message || "Încearcă din nou.", variant: "destructive" });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSignOut = async () => {
    try {
      // Clean up auth state first
      cleanupAuthState();
      
      // Attempt global sign out
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch {
        // Continue even if this fails
      }
      
      // Force page reload for clean state
      window.location.href = COUNTRY_ROUTES.auth(country);
    } catch (error) {
      // Force redirect even if there's an error
      window.location.href = COUNTRY_ROUTES.auth(country);
    }
  };

  const closeSheet = () => setIsOpen(false);

  // Mobile nav items - role-aware
  const navItems = [
    { to: COUNTRY_ROUTES.vehicles(country as CountryCode), label: t('all_vehicles') },
    { to: COUNTRY_ROUTES.about(country as CountryCode), label: t('about') },
    { to: COUNTRY_ROUTES.import(country as CountryCode), label: t('import_vehicles') },
    { to: COUNTRY_ROUTES.dealers(country as CountryCode), label: `✅ ${t('verified_dealers')}` },
    // For dealers section only relevant if NOT a buyer
    ...(!isBuyer ? [{ to: COUNTRY_ROUTES.forDealers(country as CountryCode), label: t('for_dealers') }] : []),
    { to: COUNTRY_ROUTES.buyerGuide(country as CountryCode), label: t('guide') },
    { to: '/ghiduri', label: `🧭 ${t('guides') || 'Ghiduri auto'}` },
    { to: COUNTRY_ROUTES.blogIndex(country as CountryCode), label: t('blog') },
    { to: COUNTRY_ROUTES.autoStats(country as CountryCode), label: `📊 ${t('statistics') || 'Statistici'}` },
    // Pricing only for dealers / not-yet-classified users (buyers don't need a paid plan)
    ...(!isBuyer ? [{ to: COUNTRY_ROUTES.pricing(country as CountryCode), label: t('pricing') }] : []),
    ...(!isLoading && user ? [
      { to: COUNTRY_ROUTES.myAlerts(country as CountryCode), label: t('my_alerts') },
    ] : []),
    // Dashboard / inventory only for dealers (not buyers)
    ...(!isLoading && user && canAccessPremium && isDealer ? [
      { to: COUNTRY_ROUTES.dashboard(country as CountryCode), label: t('dashboard') },
      { to: COUNTRY_ROUTES.myListingsV2(country as CountryCode), label: t('my_listings') }
    ] : []),
    ...(isAdmin ? [{ to: "/admin", label: "Admin" }] : []),
  ];

  return (
    <>
      <header className="sticky z-50 border-b border-muted-foreground/20 overflow-visible backdrop-blur-sm" style={{ background: 'var(--header-bg)', '--header-height': '5rem', top: 'var(--pwa-banner-h, 0px)' } as React.CSSProperties}>
        <div className="container h-20 md:h-24 flex items-center justify-between overflow-visible">
        <div className="flex items-center gap-4 overflow-visible">
          <Link to={basePath || '/'} className="flex items-center shrink-0" aria-label="RO Auto Market">
            <span className="inline-flex rounded-xl bg-white px-2 py-1.5 md:px-2.5 md:py-2 shadow-sm">
              <img
                ref={logoRevealRef}
                src={logoUrl}
                alt="RO Auto Market"
                className="h-11 md:h-14 w-auto max-w-full object-contain"
                width={641}
                height={930}
                fetchPriority="auto"
                decoding="async"
              />
            </span>
          </Link>
          
          {/* Sell Button - Always visible. Buyers are redirected to pricing (become dealer). */}
          <Button 
            asChild 
            size="sm" 
            variant="outline"
            className="font-semibold px-3 md:px-4"
          >
            <Link 
              to={
                isBuyer && !isDealer
                  ? COUNTRY_ROUTES.pricing(country as CountryCode)
                  : '/dealeri'
              } 



              className="flex items-center gap-1.5"
            >
              <Plus size={16} />
              <span className="hidden sm:inline">
                {isBuyer && !isDealer
                  ? (country === 'UK' ? 'Become a dealer' : country === 'DE' ? 'Händler werden' : 'Devino dealer')
                  : (country === 'UK' ? 'List stock' : country === 'DE' ? 'Bestand listen' : 'Adaugă stocul tău')}
              </span>
              <span className="sm:hidden">
                {isBuyer && !isDealer
                  ? (country === 'UK' ? 'Dealer' : country === 'DE' ? 'Händler' : 'Dealer')
                  : (country === 'UK' ? 'List' : country === 'DE' ? 'Listen' : 'Listează')}
              </span>
            </Link>
          </Button>

          {/* Private seller quick-list button — visible above the fold */}
          <Button
            asChild
            size="sm"
            variant="outline"
            className="border-brand-gold/70 text-brand-gold hover:bg-brand-gold/10 font-semibold px-2.5 md:px-3"
          >
            <Link
              to={country === 'UK' ? '/uk/vinde-masina' : country === 'DE' ? '/de/vinde-masina' : '/vinde-masina'}
              className="flex items-center gap-1.5"
            >
              <Plus size={16} />
              <span className="hidden xs:inline sm:inline">
                {country === 'UK' ? 'Sell my car' : country === 'DE' ? 'Auto verkaufen' : 'Vinde mașina'}
              </span>
              <span className="xs:hidden sm:hidden inline">
                {country === 'DE' ? 'Verkaufen' : 'Vinde'}
              </span>
            </Link>
          </Button>

          

          
          {/* Country & Currency Indicator for UK/DE */}
          {country !== 'RO' && (
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20">
              <span className="text-2xl">{config.flag}</span>
              <div className="flex flex-col leading-tight">
                <span className="text-xs font-semibold text-primary">
                  {country === 'UK' ? 'UK marketplace' : country === 'DE' ? 'DE Fahrzeugbörse' : country}
                </span>
                <span className="text-[10px] text-muted-foreground">{config.currency}</span>
              </div>
            </div>
          )}
        </div>

        {/* Desktop Navigation — max 6 elements visible + "Mai mult" dropdown */}
        <nav className="hidden md:flex items-center gap-1 text-sm">
          <Suspense fallback={<div className={`${linkClass} opacity-60`}>{t('vehicles')}</div>}>
            <MegaMenu triggerLabel={t('vehicles')} />
          </Suspense>
          
          
          
          <NavLink 
            to={COUNTRY_ROUTES.blogIndex(country as CountryCode)}
            className={`${linkClass} nav-link-animated ${isActivePath(COUNTRY_ROUTES.blogIndex(country as CountryCode)) ? 'active-link' : ''}`}
          >
            {t('blog')}
          </NavLink>

          <NavLink 
            to={COUNTRY_ROUTES.about(country as CountryCode)}
            className={`${linkClass} nav-link-animated ${isActivePath(COUNTRY_ROUTES.about(country as CountryCode)) ? 'active-link' : ''}`}
          >
            {t('about')}
          </NavLink>
          
          <NavLink 
            to={COUNTRY_ROUTES.pricing(country as CountryCode)}
            className={`${linkClass} nav-link-animated ${isActivePath(COUNTRY_ROUTES.pricing(country as CountryCode)) ? 'active-link' : ''}`}
          >
            {t('pricing')}
          </NavLink>

          
          
          {/* "Mai mult" dropdown — grupează elementele secundare */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className={`${linkClass} gap-1`} aria-label={country === 'UK' ? 'More menu options' : country === 'DE' ? 'Weitere Menüoptionen' : 'Mai multe opțiuni din meniu'}>
                {country === 'UK' ? 'More options' : country === 'DE' ? 'Weitere Optionen' : 'Mai multe opțiuni'} <ChevronDown size={16} aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56 bg-background z-50">
              <DropdownMenuItem asChild>
                <NavLink to={COUNTRY_ROUTES.import(country as CountryCode)} className="w-full cursor-pointer">
                  {t('import_vehicles')}
                </NavLink>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <NavLink to={COUNTRY_ROUTES.buyerGuide(country as CountryCode)} className="w-full cursor-pointer">
                  📖 {t('guide')}
                </NavLink>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <NavLink to="/ghiduri" className="w-full cursor-pointer">
                  🧭 {t('guides') || 'Ghiduri auto'}
                </NavLink>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <NavLink to={COUNTRY_ROUTES.autoStats(country as CountryCode)} className="w-full cursor-pointer">
                  📊 {t('statistics') || 'Statistici'}
                </NavLink>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a 
                  href="https://www.autocompare.ro?utm_source=roautomarket&utm_medium=referral&utm_campaign=navbar"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full cursor-pointer text-primary font-semibold"
                >
                  🧮 AutoCompare
                </a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {user && <NotificationCenter />}
          
          <Button variant="ghost" size="icon" asChild className="relative">
            <Link to={COUNTRY_ROUTES.favorites(country as CountryCode)} aria-label={t('favorites')}>
              <Heart className="w-5 h-5" />
              {user && favoritesCount > 0 && (
                <Badge 
                  variant="destructive" 
                  className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
                >
                  {favoritesCount}
                </Badge>
              )}
            </Link>
          </Button>

          <Button variant="ghost" size="icon" asChild className="relative">
            <Link to={COUNTRY_ROUTES.messages(country as CountryCode)} aria-label={t('messages')}>
              <MessageCircle className="w-5 h-5" />
              {user && unreadMessagesCount > 0 && (
                <Badge 
                  variant="destructive" 
                  className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
                >
                  {unreadMessagesCount}
                </Badge>
              )}
            </Link>
          </Button>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                aria-label="Toggle theme"
              >
                <Sun className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{theme === "dark" ? t('light_mode') : t('dark_mode')}</p>
            </TooltipContent>
          </Tooltip>
          
          <CountrySelector />
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className={linkClass}>
                {user ? t('account') : t('sign_in')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-background z-50">
              {user ? (
                <>
                  {/* Dealer-only: Dashboard + Inventar */}
                  {canAccessPremium && isDealer && (
                    <>
                      <DropdownMenuItem asChild>
                        <NavLink to={COUNTRY_ROUTES.dashboard(country as CountryCode)} className="w-full cursor-pointer">
                          {t('dashboard')}
                        </NavLink>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <NavLink to={COUNTRY_ROUTES.myListingsV2(country as CountryCode)} className="w-full cursor-pointer">
                          {t('my_listings')}
                        </NavLink>
                      </DropdownMenuItem>
                    </>
                  )}
                  {/* Buyer-focused shortcuts */}
                  {isBuyer && !isDealer && (
                    <DropdownMenuItem asChild>
                      <NavLink to={COUNTRY_ROUTES.favorites(country as CountryCode)} className="w-full cursor-pointer">
                        ❤️ {t('favorites')}
                      </NavLink>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem asChild>
                    <NavLink to={COUNTRY_ROUTES.myAlerts(country as CountryCode)} className="w-full cursor-pointer">
                      {t('my_alerts')}
                    </NavLink>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <NavLink to={COUNTRY_ROUTES.messages(country as CountryCode)} className="w-full cursor-pointer">
                      {t('messages')}
                    </NavLink>
                  </DropdownMenuItem>
                  {/* Buyer upsell: become a dealer */}
                  {isBuyer && !isDealer && (
                    <DropdownMenuItem asChild>
                      <NavLink to={COUNTRY_ROUTES.pricing(country as CountryCode)} className="w-full cursor-pointer text-primary font-semibold">
                        🚀 {country === 'UK' ? 'Become a dealer' : country === 'DE' ? 'Händler werden' : 'Devino dealer'}
                      </NavLink>
                    </DropdownMenuItem>
                  )}
                  {/* Sync subscription only relevant for dealers */}
                  {isDealer && (
                    <DropdownMenuItem asChild>
                      <button onClick={handleSyncSubscription} disabled={isLoading || isSyncing} className="w-full text-left">
                        {isSyncing ? t('syncing') : t('sync_subscription')}
                      </button>
                    </DropdownMenuItem>
                  )}
                  {isAdmin && (
                    <>
                      <DropdownMenuItem asChild>
                        <NavLink to="/admin" className="w-full cursor-pointer">
                          Admin
                        </NavLink>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <NavLink to="/admin/listings-review" className="w-full cursor-pointer">
                          🔎 Anunțuri în verificare
                        </NavLink>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuItem asChild>
                    <button onClick={handleSignOut} className="w-full text-left">
                      {t('logout')}
                    </button>
                  </DropdownMenuItem>
                </>
              ) : (
                <>
                  <DropdownMenuItem asChild>
                    <NavLink to={COUNTRY_ROUTES.auth(country)} className="w-full cursor-pointer">
                      {t('authentication')}
                    </NavLink>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <NavLink to={COUNTRY_ROUTES.pricing(country as CountryCode)} className="w-full cursor-pointer">
                      {t('pricing')}
                    </NavLink>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        {/* Mobile Navigation */}
        <div className="flex items-center gap-1 md:hidden shrink-0">
          
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label={t('open_menu')}>
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
          <SheetContent side="right" className="w-72 bg-background border-l z-50 overflow-y-auto">
            {/* Country/Language Selector - Three flags */}
            <div className="px-4 py-4 border-b border-border mb-4">
              <div className="text-xs font-semibold text-muted-foreground mb-3 text-center">
                {t('language')}
              </div>
              <div className="flex justify-center gap-2">
                <Button
                  variant={country === 'RO' ? 'default' : 'outline'}
                  size="lg"
                  onClick={() => {
                    setCountryCookie('RO' as CountryCode);
                    navigate('/');
                    closeSheet();
                  }}
                  className="text-2xl px-4 py-6"
                >
                  🇷🇴
                </Button>
                <Button
                  variant={country === 'UK' ? 'default' : 'outline'}
                  size="lg"
                  onClick={() => {
                    setCountryCookie('UK' as CountryCode);
                    navigate('/uk');
                    closeSheet();
                  }}
                  className="text-2xl px-4 py-6"
                >
                  🇬🇧
                </Button>
                <Button
                  variant={country === 'DE' ? 'default' : 'outline'}
                  size="lg"
                  onClick={() => {
                    setCountryCookie('DE' as CountryCode);
                    navigate('/de');
                    closeSheet();
                  }}
                  className="text-2xl px-4 py-6"
                >
                  🇩🇪
                </Button>
              </div>
            </div>
            
            <nav className="flex flex-col gap-4 pb-8">
              {navItems.map((item) => (
                <NavLink 
                  key={item.to}
                  to={item.to} 
                  onClick={closeSheet}
                  className={`${linkClass} justify-start ${isActivePath(item.to) ? 'bg-accent underline' : ''}`}
                >
                  {item.label}
                </NavLink>
              ))}
              
              {/* AutoCompare external link */}
              <a 
                href="https://www.autocompare.ro?utm_source=roautomarket&utm_medium=referral&utm_campaign=navbar"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeSheet}
                className={`${linkClass} justify-start text-primary font-semibold`}
              >
                🧮 AutoCompare
              </a>
              {/* Buyer upsell on mobile */}
              {user && isBuyer && !isDealer && (
                <NavLink 
                  to={COUNTRY_ROUTES.pricing(country as CountryCode)}
                  onClick={closeSheet}
                  className={`${linkClass} justify-start bg-primary text-primary-foreground hover:bg-primary/90 ${isActivePath(COUNTRY_ROUTES.pricing(country as CountryCode)) ? 'underline' : ''}`}
                >
                  🚀 {country === 'UK' ? 'Become a dealer' : country === 'DE' ? 'Händler werden' : 'Devino dealer'}
                </NavLink>
              )}
              
              <NavLink 
                to="/favorites"
                onClick={closeSheet}
                className={`${linkClass} justify-start relative ${isActivePath('/favorites') ? 'bg-accent underline' : ''}`}
              >
                <Heart size={16} className="mr-2" />
                {t('favorites')}
                {user && favoritesCount > 0 && (
                  <Badge variant="destructive" className="ml-auto">
                    {favoritesCount}
                  </Badge>
                )}
              </NavLink>

              <NavLink 
                to="/messages"
                onClick={closeSheet}
                className={`${linkClass} justify-start relative ${isActivePath('/messages') ? 'bg-accent underline' : ''}`}
              >
                <MessageCircle size={16} className="mr-2" />
                {t('messages')}
                {user && unreadMessagesCount > 0 && (
                  <Badge variant="destructive" className="ml-auto">
                    {unreadMessagesCount}
                  </Badge>
                )}
              </NavLink>

              <button
                onClick={() => { setTheme(theme === "dark" ? "light" : "dark"); closeSheet(); }}
                className={`${linkClass} justify-start`}
              >
                {theme === "dark" ? `🌞 ${t('light_mode')}` : `🌙 ${t('dark_mode')}`}
              </button>
              
              {user && isDealer && (
                <button
                  onClick={async () => { await handleSyncSubscription(); closeSheet(); }}
                  className={`${linkClass} justify-start`}
                  disabled={isLoading || isSyncing}
                >
                  {isSyncing ? t('syncing') : t('sync_subscription')}
                </button>
              )}
              {user ? (
                <button 
                  onClick={() => {
                    handleSignOut();
                    closeSheet();
                  }} 
                  className={`${linkClass} justify-start underline`}
                >
                  {t('logout')}
                </button>
              ) : (
                <NavLink 
                  to={COUNTRY_ROUTES.auth(country)} 
                  onClick={closeSheet}
                  className={`${linkClass} justify-start ${isActivePath(COUNTRY_ROUTES.auth(country)) ? 'bg-accent underline' : ''}`}
                >
                  {t('authentication')}
                </NavLink>
              )}
            </nav>
          </SheetContent>
        </Sheet>
        </div>
        </div>
      </header>
    </>
  );
}

